import { useEffect, useRef } from 'react'
import { Button, Collapse, Input, Segmented, Spin, Typography } from 'antd'
import type { TextAreaRef } from 'antd/es/input/TextArea'
import { CommentOutlined, DeleteOutlined, RobotOutlined, SendOutlined, UserOutlined } from '@ant-design/icons'
import { ChatMarkdown } from '../../components/ChatMarkdown'
import '../../styles/workspace.css'
import './AgentSettings.css'

export type AgentDebugMessage = {
  role: 'user' | 'assistant'
  content: string
}

export type OrchestrationStep = {
  step: number
  action: 'invoke_agent' | 'finish'
  agent_code?: string
  agent_name?: string
  task?: string
  reasoning?: string
  sub_reply_preview?: string
  reply_preview?: string
  error?: string
}

const LOCALE_OPTIONS = [
  { label: '简体', value: 'zh-CN' },
  { label: 'EN', value: 'en' },
  { label: '繁體', value: 'zh-TW' },
]

function stepTitle(step: OrchestrationStep): string {
  if (step.action === 'finish') return '输出最终回复'
  return step.agent_name || step.agent_code || '子智能体'
}

function stepDetail(step: OrchestrationStep): string {
  const parts: string[] = []
  if (step.reasoning) parts.push(step.reasoning)
  if (step.task) parts.push(`任务：${step.task}`)
  if (step.error) parts.push(step.error)
  else if (step.sub_reply_preview) parts.push(step.sub_reply_preview)
  else if (step.reply_preview) parts.push(step.reply_preview)
  return parts.join('\n\n')
}

type Props = {
  agentName?: string
  agentCode?: string
  locale: string
  onLocaleChange: (locale: string) => void
  messages: AgentDebugMessage[]
  input: string
  onInputChange: (value: string) => void
  onSend: () => void
  onClear: () => void
  loading: boolean
  orchestrationSteps?: OrchestrationStep[]
}

export function AgentDebugChat({
  agentName,
  agentCode,
  locale,
  onLocaleChange,
  messages,
  input,
  onInputChange,
  onSend,
  onClear,
  loading,
  orchestrationSteps = [],
}: Props) {
  const bottomRef = useRef<HTMLDivElement>(null)
  const composerRef = useRef<TextAreaRef>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [messages, loading, orchestrationSteps])

  const canSend = Boolean(input.trim()) && !loading
  const showEmpty = messages.length === 0 && !loading && orchestrationSteps.length === 0

  const orchestrationPanel =
    orchestrationSteps.length > 0 ? (
      <Collapse
        bordered={false}
        className="agent-orchestration-collapse"
        defaultActiveKey={['steps']}
        items={[
          {
            key: 'steps',
            label: `编排过程（${orchestrationSteps.length} 步）`,
            children: (
              <div className="agent-orchestration-steps">
                {orchestrationSteps.map((s) => {
                  const detail = stepDetail(s)
                  const isError = Boolean(s.error)
                  const isFinish = s.action === 'finish'
                  return (
                    <div
                      key={`${s.step}-${s.action}-${s.agent_code || 'finish'}`}
                      className={`agent-orchestration-step${isError ? ' agent-orchestration-step-error' : ''}${
                        isFinish ? ' agent-orchestration-step-finish' : ''
                      }`}
                    >
                      <div className="agent-orchestration-step-index">{s.step}</div>
                      <div className="agent-orchestration-step-main">
                        <div className="agent-orchestration-step-label">
                          {isFinish ? '完成' : '调用'}
                          <span>{stepTitle(s)}</span>
                        </div>
                        {detail ? (
                          <Typography.Paragraph type="secondary" className="agent-orchestration-step-detail">
                            {detail}
                          </Typography.Paragraph>
                        ) : null}
                      </div>
                    </div>
                  )
                })}
              </div>
            ),
          },
        ]}
      />
    ) : null

  return (
    <div className="agent-debug-chat">
      <div className="agent-debug-chat-toolbar">
        <div>
          <Typography.Text strong>对话调试</Typography.Text>
          <Typography.Text type="secondary" className="agent-debug-chat-sub">
            {agentName ? `${agentName} · ` : ''}
            使用当前表单提示词，无需先保存
          </Typography.Text>
        </div>
        <div className="agent-debug-chat-toolbar-actions">
          <Segmented size="small" value={locale} options={LOCALE_OPTIONS} onChange={(v) => onLocaleChange(String(v))} />
          <Button size="small" icon={<DeleteOutlined />} onClick={onClear} disabled={messages.length === 0 && !input}>
            清空
          </Button>
        </div>
      </div>

      <div className="agent-debug-chat-body">
        {showEmpty ? (
          <div className="chat-empty agent-debug-chat-empty">
            <div className="chat-empty-icon">
              <CommentOutlined />
            </div>
            <h3>试试和智能体对话</h3>
            <p>
              在下方输入问题，验证当前语言下的提示词效果。Enter 发送，Shift+Enter 换行。
              {agentCode === 'graph_extraction' ? (
                <>
                  {' '}
                  图谱抽取调试请粘贴一段文档正文（含标题/段落），模型将返回 entities/relations 的 JSON。
                </>
              ) : null}
            </p>
          </div>
        ) : (
          <div className="chat-messages agent-debug-chat-messages">
            {messages.map((m, i) => {
              const isUser = m.role === 'user'
              const isLastAssistant =
                !isUser && i === messages.length - 1 && orchestrationSteps.length > 0 && !loading
              return (
                <div key={`${m.role}-${i}`}>
                  <div className={`chat-msg${isUser ? ' chat-msg-user' : ''}`}>
                    <div
                      className={`chat-msg-avatar ${
                        isUser ? 'chat-msg-avatar-user' : 'chat-msg-avatar-assistant'
                      }`}
                    >
                      {isUser ? <UserOutlined /> : <RobotOutlined />}
                    </div>
                    <div className="chat-msg-main">
                      <div
                        className={`chat-msg-bubble ${
                          isUser ? 'chat-msg-bubble-user' : 'chat-msg-bubble-assistant'
                        }`}
                      >
                        {isUser ? m.content : <ChatMarkdown content={m.content} />}
                      </div>
                    </div>
                  </div>
                  {isLastAssistant ? orchestrationPanel : null}
                </div>
              )
            })}
            {loading ? (
              <div className="chat-msg">
                <div className="chat-msg-avatar chat-msg-avatar-assistant">
                  <RobotOutlined />
                </div>
                <div className="chat-msg-main">
                  <div className="chat-msg-bubble chat-msg-bubble-assistant chat-msg-loading">
                    <Spin size="small" /> 正在生成回复…
                  </div>
                </div>
              </div>
            ) : null}
            {!messages.some((m) => m.role === 'assistant') && orchestrationPanel}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      <div className="agent-debug-chat-footer">
        <div
          className="workspace-composer-inner agent-debug-composer"
          onMouseDown={(e) => {
            const el = e.target as HTMLElement
            if (el.closest('button')) return
            if (!el.closest('textarea')) composerRef.current?.focus()
          }}
        >
          <Input.TextArea
            ref={composerRef}
            classNames={{ textarea: 'chat-composer-input' }}
            autoSize={{ minRows: 1, maxRows: 4 }}
            value={input}
            onChange={(e) => onInputChange(e.target.value)}
            placeholder="输入测试问题，Enter 发送"
            disabled={loading}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault()
                if (canSend) onSend()
              }
            }}
          />
          <Button type="primary" icon={<SendOutlined />} loading={loading} disabled={!canSend} onClick={onSend}>
            发送
          </Button>
        </div>
      </div>
    </div>
  )
}
